// Sprint S20.5 PR-3 (D013-EC-69) — spawn error → founder copy mapper.
//
// The backend rejects `POST /api/agents` (and the lazy first-message
// spawn) with structured `AppError` codes — `NO_RUNNER_AVAILABLE`,
// `RUNNER_NOT_READY`, `CLAUDE_NOT_AUTHENTICATED`, `RUNNER_OFFLINE`,
// etc. The frontend used to render a single generic "Could not create
// the conversation. Tap to retry." toast for every branch. A founder
// who can't read code has no signal about what to do next.
//
// This helper centralises the mapping so:
//
//   - Every call site (Sidebar "+" button, Composer first-send) goes
//     through one switch — consistent copy + intent.
//   - Adding a new code is a one-line edit here, not a hunt across
//     UI files.
//   - Tests pin the contract (each known code maps to its key + intent;
//     unknown codes fall back to GENERIC + retry).
//
// `intent` drives the toast action:
//   - 'retry'  → "Tap to retry" — re-runs the same spawn.
//   - 'usage'  → "View runner usage" — opens the centralized usage
//                modal so the founder can see WHICH cap is exhausted
//                and how long until reset.
//   - 'silent' → no action; the toast is informational only.

import type { TKey } from '../i18n';

export type SpawnErrorCode =
  | 'NO_RUNNER_AVAILABLE'
  | 'RUNNER_NOT_READY'
  | 'CLAUDE_NOT_AUTHENTICATED'
  | 'RUNNER_OFFLINE'
  | 'GENERIC';

export type SpawnErrorIntent = 'retry' | 'usage' | 'silent';

export interface SpawnErrorMapping {
  code: SpawnErrorCode;
  i18nKey: TKey;
  intent: SpawnErrorIntent;
}

const TABLE: Record<SpawnErrorCode, SpawnErrorMapping> = {
  NO_RUNNER_AVAILABLE: {
    code: 'NO_RUNNER_AVAILABLE',
    i18nKey: 'errors.spawn.NO_RUNNER_AVAILABLE',
    intent: 'usage',
  },
  RUNNER_NOT_READY: {
    code: 'RUNNER_NOT_READY',
    i18nKey: 'errors.spawn.RUNNER_NOT_READY',
    intent: 'retry',
  },
  CLAUDE_NOT_AUTHENTICATED: {
    code: 'CLAUDE_NOT_AUTHENTICATED',
    i18nKey: 'errors.spawn.CLAUDE_NOT_AUTHENTICATED',
    intent: 'silent',
  },
  RUNNER_OFFLINE: {
    code: 'RUNNER_OFFLINE',
    i18nKey: 'errors.spawn.RUNNER_OFFLINE',
    intent: 'retry',
  },
  GENERIC: {
    code: 'GENERIC',
    i18nKey: 'errors.spawn.GENERIC',
    intent: 'retry',
  },
};

// Resolve any thrown error (or raw code string) to its founder-mode
// mapping. Unknown codes fall back to GENERIC so the legacy toast
// surface is preserved verbatim.
export function mapSpawnError(err: unknown): SpawnErrorMapping {
  const raw =
    typeof err === 'string'
      ? err
      : ((err as { code?: unknown } | null)?.code as string | undefined);
  if (raw && raw in TABLE) {
    return TABLE[raw as SpawnErrorCode];
  }
  return TABLE.GENERIC;
}

// Test-only export — lets the i18n parity test enumerate every known
// code without exposing the table to runtime callers.
export const __KNOWN_SPAWN_CODES = Object.keys(TABLE) as SpawnErrorCode[];
